/**
 * Inventory Formatting Utilities
 *
 * Shared functions for formatting inventory-related data for display
 */

import type { InventoryStatus } from "@/lib/types/inventory";

/**
 * Canonical inventory status display config
 * Single source of truth for status labels and styling across all views
 */
export const INVENTORY_STATUS_CONFIG: Record<
  InventoryStatus,
  {
    label: string;
    badgeVariant: "success" | "warning" | "danger";
    className: string;
  }
> = {
  in_stock: {
    label: "In Stock",
    badgeVariant: "success",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  reorder_needed: {
    label: "Reorder",
    badgeVariant: "warning",
    className: "bg-amber-100 text-amber-800 border-amber-200",
  },
  sold_out: {
    label: "Out of Stock",
    badgeVariant: "danger",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

/**
 * Get display config for an inventory status
 */
export function getStatusConfig(status: InventoryStatus) {
  return INVENTORY_STATUS_CONFIG[status] ?? INVENTORY_STATUS_CONFIG.sold_out;
}

/**
 * Format FDA date string (YYYYMMDD) for display
 *
 * @example
 * formatFdaDate("20240115") // "01/15/2024"
 * formatFdaDate(undefined) // "N/A"
 */
export function formatFdaDate(dateStr?: string): string {
  if (!dateStr || dateStr.length !== 8) return "N/A";
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${month}/${day}/${year}`;
}

/**
 * Map DEA schedule code to display name, with non-controlled fallback
 * Handles empty/null values unlike mapDEAScheduleName
 *
 * @example
 * getDEAScheduleLabel("CII") // "Schedule II"
 * getDEAScheduleLabel("") // "Non-Controlled"
 * getDEAScheduleLabel(null) // "Non-Controlled"
 */
export function getDEAScheduleLabel(
  schedule: string | null | undefined
): string {
  if (!schedule || schedule.trim() === "") return "Non-Controlled";
  return mapDEAScheduleName(schedule);
}

/**
 * Multi-value filter function for TanStack Table columns
 * Used for columns with faceted filters (status, type, etc.)
 */
export function multiValueFilterFn(
  row: { getValue: (id: string) => unknown },
  id: string,
  value: unknown[]
): boolean {
  return value.includes(row.getValue(id));
}

/**
 * Format drug schedule for display
 * Converts database enum format to human-readable format
 *
 * @example
 * formatDrugSchedule("schedule_ii") // "Schedule II"
 * formatDrugSchedule("schedule_iii") // "Schedule III"
 * formatDrugSchedule(null) // "Non-Controlled"
 */
export function formatDrugSchedule(
  drugType: string | null | undefined
): string {
  if (!drugType) return "Non-Controlled";

  // Handle database format (schedule_ii, schedule_iii, etc.)
  if (drugType.includes("_")) {
    return drugType
      .split("_")
      .map((word) => {
        // Keep Roman numerals uppercase (ii, iii, iv, v)
        if (/^[iv]+$/i.test(word)) {
          return word.toUpperCase();
        }
        // Capitalize first letter of other words
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  }

  return drugType;
}

/**
 * Extract just the Roman numeral from a drug schedule
 * Used for compact badge display in tables
 *
 * @example
 * getScheduleNumeral("schedule_ii") // "II"
 * getScheduleNumeral("schedule_iii") // "III"
 * getScheduleNumeral(null) // null
 */
export function getScheduleNumeral(
  drugType: string | null | undefined
): string | null {
  if (!drugType) return null;

  const numeralMap: Record<string, string> = {
    schedule_ii: "II",
    schedule_iii: "III",
    schedule_iv: "IV",
    schedule_v: "V",
  };

  return numeralMap[drugType] ?? null;
}

/**
 * Extract Roman numeral from a DEA schedule code
 *
 * @example
 * getDEAScheduleNumeral("CII") // "II"
 * getDEAScheduleNumeral("CIII") // "III"
 * getDEAScheduleNumeral("") // null
 */
export function getDEAScheduleNumeral(
  schedule: string | null | undefined
): string | null {
  if (!schedule || schedule.trim() === "") return null;

  const numeralMap: Record<string, string> = {
    CII: "II",
    CIII: "III",
    CIV: "IV",
    CV: "V",
  };

  return numeralMap[schedule] ?? null;
}

/**
 * Map DEA schedule codes to display names
 * Used for data from external APIs (OpenFDA, RxNorm)
 *
 * @example
 * mapDEAScheduleName("CII") // "Schedule II"
 * mapDEAScheduleName("CIII") // "Schedule III"
 */
export function mapDEAScheduleName(schedule: string): string {
  const scheduleMap: Record<string, string> = {
    CII: "Schedule II",
    CIII: "Schedule III",
    CIV: "Schedule IV",
    CV: "Schedule V",
  };
  return scheduleMap[schedule] || schedule;
}

/**
 * Convert display format to database enum
 *
 * @example
 * mapScheduleToEnum("Schedule II") // "schedule_ii"
 * mapScheduleToEnum("Schedule III") // "schedule_iii"
 */
export function mapScheduleToEnum(schedule: string): string {
  const mapping: Record<string, string> = {
    "Schedule II": "schedule_ii",
    "Schedule III": "schedule_iii",
    "Schedule IV": "schedule_iv",
    "Schedule V": "schedule_v",
  };
  return mapping[schedule] || "schedule_ii";
}

/**
 * Convert database enum to display format
 *
 * @example
 * mapEnumToSchedule("schedule_ii") // "Schedule II"
 * mapEnumToSchedule("schedule_iii") // "Schedule III"
 */
export function mapEnumToSchedule(drugType: string): string {
  const mapping: Record<string, string> = {
    schedule_ii: "Schedule II",
    schedule_iii: "Schedule III",
    schedule_iv: "Schedule IV",
    schedule_v: "Schedule V",
  };
  return mapping[drugType] || "Schedule II";
}
